import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { forwardRef, Suspense, useEffect, useMemo, useRef } from 'react';
import { Color, DoubleSide, Texture } from 'three';
import { FONT_TYPES, ITextAlignment } from '../../component-data-structure';
import DreiText from '../DreiText';
import { CurvedEntityGeometry } from './CurvedEntityGeometry';
import { getRadiusFromCurvature } from './gizmoUtil';
import { MeshChromaKeyMaterial } from './MeshChromaKeyMaterial';
import { AlphaMaterial } from '../customMaterials';
const isSafari = (navigator.userAgent.indexOf('Safari') != -1 || navigator.userAgent.includes('AppleWebKit')) && navigator.userAgent.indexOf('Chrome') == -1;
const TEXT_ENTITY_PADDING = 0.027;
const getEntityTextPos = (text, scale) => {
    const { textAlignment } = text;
    let xPos;
    let anchorXPos;
    switch (textAlignment) {
        case ITextAlignment.left: {
            xPos = -scale[0] / 2 + TEXT_ENTITY_PADDING;
            anchorXPos = 'left';
            break;
        }
        case ITextAlignment.right: {
            xPos = scale[0] / 2 - TEXT_ENTITY_PADDING;
            anchorXPos = 'right';
            break;
        }
        default: {
            xPos = 0;
            anchorXPos = 'center';
        }
    }
    const textPos = [xPos, 0, 0];
    return { textPos, anchorXPos };
};
const CurvedEntityMaterial = ({ stackedAlpha, hasAlpha, opacity, side, videoTexture, onBeforeCompile, imageTexture, innerColor, chromaKey }) => {
    // if(!!chromaKey) console.log('getMaterial: stackedAlpha: ', stackedAlpha, 'hasAlpha: ', hasAlpha, '!!videoTexture: ', !!videoTexture, '!!imageTexture: ', !!imageTexture);
    // Stacked alpha video
    if (stackedAlpha && !!videoTexture) {
        // console.log('stacked alpha video');
        return _jsx(AlphaMaterial, { packedTexture: videoTexture || new Texture(), transparent: true, opacity: opacity, depthWrite: false, side: side });
    }
    // Video that isn't alpha and doesn't have choma key active
    if (!hasAlpha && !!videoTexture) {
        // console.log('non alpha no chroma key video');
        return _jsx("meshBasicMaterial", { onBeforeCompile: onBeforeCompile, map: videoTexture, side: side, opacity: opacity, transparent: true, wireframe: false, depthWrite: false });
    }
    // Video that isn't alpha but does have chroma key active
    if (!!videoTexture) {
        // console.log('Video texture')
        if (hasAlpha && !!chromaKey && !!chromaKey.color) {
            // console.log('non alpha, does have chroma key video');
            return (
            // <meshBasicMaterial map={videoTexture} side={side} opacity={opacity} transparent={true} wireframe={false} depthWrite={false} />
            _jsx(MeshChromaKeyMaterial, { onBeforeCompile: onBeforeCompile, uniforms: {
                    map: { value: videoTexture },
                    similarity: { value: chromaKey?.similarity },
                    smoothness: { value: chromaKey?.smoothness },
                    spill: { value: chromaKey?.spill },
                    keyColor: { value: chromaKey?.color },
                }, needsUpdate: true, opacity: opacity, transparent: true, side: side, uniformsNeedUpdate: true, depthWrite: false }));
        }
        else {
            // console.log('NO chroma key');
            return _jsx("meshBasicMaterial", { onBeforeCompile: onBeforeCompile, map: videoTexture, side: side, opacity: opacity, transparent: true, wireframe: false, depthWrite: false });
        }
    }
    // Non video
    if (!!imageTexture) {
        // console.log('Image material');
        if (!!chromaKey && !!chromaKey.color && hasAlpha) {
            // console.log('chroma key, data: ', chromaKey)
            return (_jsx(MeshChromaKeyMaterial
            //onBeforeCompile={onBeforeCompile}
            , { 
                //onBeforeCompile={onBeforeCompile}
                uniforms: {
                    map: { value: imageTexture },
                    similarity: { value: chromaKey?.similarity },
                    smoothness: { value: chromaKey?.smoothness },
                    spill: { value: chromaKey?.spill },
                    keyColor: { value: chromaKey?.color },
                }, needsUpdate: true, opacity: opacity, transparent: true, side: side, uniformsNeedUpdate: true, depthWrite: false }));
        }
        else {
            // console.log('NO chroma key');
            return _jsx("meshBasicMaterial", { color: innerColor, map: imageTexture, side: side, opacity: opacity, transparent: true, wireframe: false, depthWrite: false });
        }
    }
    // console.log('No image or video, plain color material.')
    return _jsx("meshBasicMaterial", { color: innerColor, side: side, opacity: opacity, transparent: true, wireframe: false, depthWrite: false });
};
const CurvedEntity = forwardRef(({ scale, visible = true, imageTexture, videoTexture, position, cornerRadius = 0, opacity = 1, colorRgba, curvature, renderOrder = 1, hasAlpha = false, stackedAlpha = false, raycast, text, isTextScaleLocked, border, name = 'Curved Entity', side = DoubleSide, onPointerDown, onPointerUp, onDoubleClick, chromaKey, castShadow = false, receiveShadow = false, onTextSync, onMount }, ref) => {
    // All cases
    const width = scale[0];
    const widthSegments = Math.floor(Math.abs(width) * 80);
    const height = scale[1];
    const radius = getRadiusFromCurvature(curvature, width) * Math.sign(curvature);
    const innerColor = !!colorRgba ? new Color(`rgb(${colorRgba[0]}, ${colorRgba[1]}, ${colorRgba[2]})`) : undefined;
    const textWrapperGroupRef = useRef(null);
    const prevXScale = useRef(0.32);
    const hasRunRef = useRef(false);
    const outerRef = useRef(null);
    //const initialScaleRef = useRef(1/scale[0]);
    const onBeforeCompile = (shader) => {
        if (!isSafari)
            return;
        shader.fragmentShader = shader.fragmentShader.replace('#include <map_fragment>', `
			#ifdef USE_MAP
			
				vec4 sampledDiffuseColor = texture2D( map, vMapUv );
			
				// inline sRGB decode
				sampledDiffuseColor = vec4( mix( pow( sampledDiffuseColor.rgb * 0.9478672986 + vec3( 0.0521327014 ), vec3( 2.4 ) ), sampledDiffuseColor.rgb * 0.0773993808, vec3( lessThanEqual( sampledDiffuseColor.rgb, vec3( 0.04045 ) ) ) ), sampledDiffuseColor.a );
		
				diffuseColor *= sampledDiffuseColor;
		
			#endif
		
			`);
    };
    useEffect(() => {
        onMount?.();
    }, []);
    useEffect(() => {
        if (!isTextScaleLocked)
            return;
        prevXScale.current = scale[0];
    }, [isTextScaleLocked]);
    useEffect(() => {
        hasRunRef.current = true;
        if (!isTextScaleLocked)
            return;
        const ratio = Math.abs(scale[0] / prevXScale.current);
        // if (ratio < 0) return;
        // console.log('adjust', ratio)
        prevXScale.current = scale[0];
        textWrapperGroupRef.current?.scale.multiplyScalar(ratio);
    }, [scale[0]]);
    // Border related
    const borderColor = border?.borderRgba ? new Color(`rgb(${border?.borderRgba[0]}, ${border?.borderRgba[1]}, ${border?.borderRgba[2]})`) : undefined;
    // Text related
    const fontColor = text?.fontRgba ? new Color(`rgb(${text?.fontRgba[0]}, ${text?.fontRgba[1]}, ${text?.fontRgba[2]})`) : undefined;
    const filteredArray = text ? FONT_TYPES.filter((font) => font.fontFamily === text.fontFamily) : [];
    const { textPos, anchorXPos } = text ? getEntityTextPos(text, scale) : { textPos: [0, 0, 0], anchorXPos: undefined };
    // Memoise geometries
    const curvedEntityGeometry = useMemo(() => {
        if (width === 0 || height === 0 || widthSegments === 0)
            return null;
        return _jsx(CurvedEntityGeometry, { args: [width, height, widthSegments, cornerRadius, curvature] });
    }, [width, height, cornerRadius, curvature, widthSegments]);
    const outerGeometry = useMemo(() => {
        if (width === 0 || height === 0 || widthSegments === 0)
            return null;
        return border && border.borderSize > 0 ? _jsx(CurvedEntityGeometry, { args: [width, height, widthSegments, cornerRadius, curvature, border.borderSize] }) : null;
    }, [width, height, cornerRadius, curvature, border, border?.borderSize, widthSegments]);
    if (typeof curvedEntityGeometry === null)
        return null;
    if (width === 0 || height === 0 || widthSegments === 0)
        return null;
    return (_jsx(Suspense, { fallback: null, children: _jsxs("object3D", { name: name, ref: ref, position: position, raycast: raycast, visible: visible, onPointerDown: onPointerDown, onPointerUp: onPointerUp, onDoubleClick: onDoubleClick, children: [opacity === 1.0 && !hasAlpha && (_jsxs("mesh", { castShadow: castShadow, raycast: raycast, renderOrder: 1, children: [curvedEntityGeometry, _jsx("meshBasicMaterial", { colorWrite: false, polygonOffset: true, polygonOffsetUnits: 10, polygonOffsetFactor: 10, depthWrite: true, side: side, visible: false })] })), receiveShadow && (_jsxs("mesh", { receiveShadow: true, renderOrder: renderOrder + 6, raycast: raycast, children: [curvedEntityGeometry, _jsx("shadowMaterial", { opacity: 0.2, polygonOffset: true, polygonOffsetFactor: 25, polygonOffsetUnits: 10 })] })), _jsxs("mesh", { castShadow: castShadow, renderOrder: renderOrder + 3, raycast: raycast, children: [curvedEntityGeometry, _jsx(CurvedEntityMaterial, { onBeforeCompile: onBeforeCompile, hasAlpha: hasAlpha, stackedAlpha: stackedAlpha, opacity: opacity, side: side, videoTexture: stackedAlpha ? videoTexture : videoTexture?.clone(), imageTexture: imageTexture?.clone(), innerColor: innerColor, chromaKey: chromaKey }, imageTexture ? 'img' : 'vid')] }), text && (_jsx("group", { ref: textWrapperGroupRef, children: _jsx(DreiText, { raycast: raycast, color: fontColor, alpha: text.fontRgba ? text.fontRgba[3] : 1, fontSize: text.fontSize, position: textPos, url: filteredArray[0]?.url, textAlign: text.textAlignment, fontFamily: filteredArray[0]?.fontFamily, anchorY: 'middle', anchorX: anchorXPos, maxWidth: Math.abs(scale[0]), renderOrder: renderOrder + 5, depthWrite: false, curveRadius: radius, side: typeof text.side !== 'undefined' ? text.side : DoubleSide, curvature: curvature, 
                        //onTextSync={_onTextSync}
                        preventPolygonOffset: !!text.preventPolygonOffset, children: text.value }, 'btn_text') })), border && border.borderSize > 0 && (_jsxs("mesh", { castShadow: castShadow, renderOrder: renderOrder + 2, raycast: raycast, children: [outerGeometry, _jsx("meshBasicMaterial", { color: borderColor, side: side, opacity: border.borderRgba[3], transparent: true, wireframe: false, depthWrite: false })] }))] }) }));
});
CurvedEntity.displayName = 'curved-entity';
export default CurvedEntity;
